/**
 * Size Guides State
 *
 * Channel-neutral canonical size guides (guías de talle) authored in a gallery
 * + creation wizard + spreadsheet editor. See SDD `docs/prs/TV1-116/sdd.md`
 * §"State Design".
 */

import { Column, Row, Views } from '@appTypes/Spreadsheet';

// TODO(TV1-116 #2): Swap these interim types for `SizeGuideReadmodelType` /
// related schemas from '@bett3r-dev/teselly-domain' once the size-guide domain
// types land (ticket #2). Until then this front-end-local shape mirrors the
// canonical guide doc described in the SDD §"Key Modeling Decision".

/** Measure type of a canonical guide (channel-neutral). */
export type SizeGuideMeasureType = 'BODY' | 'CLOTHING' | 'MIXED';

/** Channel sync status of a single per-channel binding. */
export type SizeGuideSyncStatus = 'draft' | 'pending' | 'synced' | 'error';

/** A flat, spreadsheet-friendly canonical column. */
export type SizeGuideColumn = {
  key: string;
  label: string;
  unit?: string;
  role: 'main' | 'equivalence' | 'measure';
  measureGroup?: string;
  rangeRole?: 'from' | 'to' | 'single';
  valueType: 'string' | 'enum' | 'number_unit';
};

/** A single canonical row — scalar cells keyed by column key. */
export type SizeGuideRow = {
  id: string;
  /**
   * The channel's own row id captured on import. For MercadoLibre this IS the
   * full `SIZE_GRID_ROW_ID` value (e.g. `"2782342:2"`) written onto a
   * publication variation. Absent for in-app rows.
   */
  remoteRowId?: string;
  cells: Record<string, string>;
};

/** A chart-level guide attribute (e.g. GENDER). */
export type SizeGuideAttribute = {
  key: string;
  label: string;
  valueType: 'enum' | 'string';
  value: string;
};

/**
 * Common MercadoLibre apparel GENDER values. Stored on a guide as a `gender`
 * guideAttribute (key `gender`) so the guide can be published to a channel
 * without prompting for it later. Sent to ML by name.
 */
export const SIZE_GUIDE_GENDER_OPTIONS = [
  'Mujer',
  'Hombre',
  'Niñas',
  'Niños',
  'Bebés',
  'Sin género'
];

/** The canonical `gender` guideAttribute key. */
export const SIZE_GUIDE_GENDER_KEY = 'gender';

/**
 * Common MercadoLibre clothing/footwear category domains that support size
 * charts. The full set (~57 on MLA) comes from the live
 * `/catalog/charts/{site}/configurations/active_domains` proxy; this curated
 * subset is an OFFLINE FALLBACK so the publish-form domain picker always offers
 * options even when the ML proxy is unreachable. The live list takes precedence
 * when it loads. `domainId` is the bare ML domain id (no site prefix) written
 * into the chart payload; labels are MLA (es-AR) display names.
 */
export const ML_SIZE_CHART_DOMAINS: { domainId: string; name: string }[] = [
  { domainId: 'T_SHIRTS', name: 'Remeras y musculosas' },
  { domainId: 'SHIRTS', name: 'Camisas' },
  { domainId: 'PANTS', name: 'Pantalones' },
  { domainId: 'JEANS', name: 'Jeans' },
  { domainId: 'SHORTS', name: 'Shorts' },
  { domainId: 'BERMUDA_SHORTS', name: 'Bermudas' },
  { domainId: 'SKIRTS', name: 'Polleras y minifaldas' },
  { domainId: 'DRESSES', name: 'Vestidos' },
  { domainId: 'JUMPSUITS', name: 'Monos y enteritos' },
  { domainId: 'SWEATERS_AND_CARDIGANS', name: 'Sweaters y cardigans' },
  { domainId: 'SWEATSHIRTS', name: 'Buzos' },
  { domainId: 'JACKETS_AND_COATS', name: 'Camperas y abrigos' },
  { domainId: 'BLAZERS', name: 'Sacos y blazers' },
  { domainId: 'VESTS', name: 'Chalecos' },
  { domainId: 'LEGGINGS', name: 'Calzas' },
  { domainId: 'TOPS', name: 'Tops y corpiños deportivos' },
  { domainId: 'BLOUSES', name: 'Blusas' },
  { domainId: 'UNDERWEAR', name: 'Ropa interior' },
  { domainId: 'BRAS', name: 'Corpiños' },
  { domainId: 'SWIMSUITS', name: 'Mallas y trajes de baño' },
  { domainId: 'PAJAMAS', name: 'Pijamas' },
  { domainId: 'SOCKS', name: 'Medias' },
  { domainId: 'SNEAKERS', name: 'Zapatillas' },
  { domainId: 'SHOES', name: 'Zapatos' },
  { domainId: 'BOOTS', name: 'Botas y botinetas' },
  { domainId: 'SANDALS', name: 'Sandalias y ojotas' }
];

/** A per-channel binding projecting the canonical guide onto a channel. */
export type SizeGuideChannelBinding = {
  connectorId: string;
  integrationUserId: string;
  domainId: string;
  measureType: string;
  mainAttributeId: string;
  attributeMap: Record<string, string>;
  guideAttributeMap: Record<string, { attrId: string; valueIdMap: Record<string, string> }>;
  remoteChartId?: string;
  syncStatus: SizeGuideSyncStatus;
  syncError?: string;
  lastSyncedAt?: string;
};

/**
 * Interim readmodel shape for a canonical size guide. Mirrors the readmodel the
 * `SizeGuideReadmodel` will project (SDD §"Readmodel — SizeGuideReadmodel").
 * Replace with the teselly-domain type once ticket #2 lands.
 */
export type SizeGuideReadmodelType = {
  id: string;
  accountId: string;
  name: string;
  measureType: SizeGuideMeasureType;
  guideAttributes: SizeGuideAttribute[];
  columns: SizeGuideColumn[];
  rows: SizeGuideRow[];
  bindings: SizeGuideChannelBinding[];
  /**
   * Set when the guide originated from an import (channel or file). Drives the
   * gallery origin tag (channel logo + channel-user name, or file icon + file
   * name). Real channel bindings (sync state) arrive separately in later
   * tickets — this is a lightweight provenance marker, NOT a binding.
   */
  importedFrom?: {
    /** 'channel' (sales-channel import) or 'file' (spreadsheet upload). */
    source?: 'channel' | 'file';
    connectorId?: string;
    /** The EXACT channel account the guide was imported from. */
    integrationUserId?: string;
    /** Display name: the channel user/nickname, or the uploaded file name. */
    label?: string;
    remoteChartId?: string;
    domainId?: string;
  };
};

/** A domain that supports charts on a connector (SDD §"ML technical-sheet proxy"). */
export type SizeGuideActiveDomain = {
  connectorId: string;
  domainId: string;
  name: string;
};

/**
 * Gallery view-model for a guide card (mirrors priceList's `GalleryItem`):
 * the readmodel plus a derived display `color`.
 */
export type SizeGuideGalleryItem = SizeGuideReadmodelType & {
  color: string;
};

/** A queued privileged CRUD op (drained by `sizeGuideCrudPolicy`). */
export type SizeGuideCrudOp =
  | { kind: 'upsert'; id: string; guide: SizeGuideReadmodelType; isImport?: boolean }
  | { kind: 'delete'; id: string };

export interface SizeGuidesState {
  // =========================================================================
  // DATA — populated from the readmodel subscription + ML specs proxy.
  // =========================================================================
  data: {
    /** Canonical guides projected by `SizeGuideReadmodel`. */
    guides: SizeGuideReadmodelType[];
    /** Gallery view-models (readmodel + derived `color`). */
    galleryItems: SizeGuideGalleryItem[];
    /** Chart-supporting domains per connector (drives the wizard). */
    activeDomains: SizeGuideActiveDomain[];
    /**
     * Server-side paginated/searchable list view-state for the gallery. The
     * data policy fetches one page at a time from the `-search` route (sorted
     * by name); `guides` holds ONLY the current page. `totalCount` is the full
     * server-side count (drives the pager). Scales to thousands of guides
     * without loading them all client-side.
     */
    list: {
      /** Current free-text search term (debounced server-side full-text). */
      searchQuery: string;
      /** 1-based current page. */
      page: number;
      /** Page size (rows per page). */
      pageSize: number;
      /** Total matching guides server-side (for the pager). */
      totalCount: number;
      /** True while a page fetch is in flight (drives the skeleton on first load). */
      loading: boolean;
    };
  };

  // =========================================================================
  // EDITOR — the SpreadsheetEditor working copy for one selected guide.
  // =========================================================================
  editor: {
    selectedGuideId: string | null;
    columns: Column[];
    rows: Row[];
    /**
     * Key (column `id`) of the canonical `role: 'main'` column for the working
     * guide. Spreadsheet `Column` carries no `role`, so the editor tracks the
     * main column explicitly. `null` until a guide is loaded/seeded.
     */
    mainColumnKey: string | null;
    /** Single default view — NOT one-view-per-guide. */
    views: Views;
    dirty: boolean;
  };

  // =========================================================================
  // LOADING — pending CRUD queue drained by `sizeGuideCrudPolicy`.
  // =========================================================================
  loading: {
    /** Privileged CRUD queue drained by `sizeGuideCrudPolicy`. */
    pendingCrud: SizeGuideCrudOp[];
  };
}

/**
 * Single default view for the size-guide editor. Mirrors `defaultPriceListViews`
 * — guides share ONE view, NOT one-view-per-guide (SDD §"State Design").
 */
export const defaultSizeGuideViews: Views = [
  { id: 'view-1', label: 'All Sizes', type: 'view', filters: [], groupBy: null, hideColumns: [], sort: null }
];

export const initialState: SizeGuidesState = {
  data: {
    guides: [],
    galleryItems: [],
    activeDomains: [],
    list: {
      searchQuery: '',
      page: 1,
      pageSize: 24,
      totalCount: 0,
      loading: true
    }
  },
  editor: {
    selectedGuideId: null,
    columns: [],
    rows: [],
    mainColumnKey: null,
    views: defaultSizeGuideViews,
    dirty: false
  },
  loading: {
    pendingCrud: []
  }
};

/**
 * Stable empty-array references for array selectors.
 *
 * Selectors are read via Zustand's `useStore`, built on React's
 * `useSyncExternalStore`. `getSnapshot` must return a referentially-stable
 * value for the same store state — returning a fresh `[]` each call makes React
 * see "snapshot changed" on every render, scheduling another render forever
 * (`Maximum update depth exceeded`). See priceList's `EMPTY_BATCHES` note.
 */
const EMPTY_COLUMNS: Column[] = [];
const EMPTY_ROWS: Row[] = [];

/**
 * Selectors — pure derivations from state.
 */
export const selectors = {
  /** The guide matching `id`, or undefined. */
  selectGuideById: (
    state: SizeGuidesState,
    id: string
  ): SizeGuideReadmodelType | undefined =>
    state.data.guides.find(( guide ) => guide.id === id ),

  /** The editor's working columns (referentially stable when empty). */
  selectEditorColumns: ( state: SizeGuidesState ): Column[] =>
    state.editor.columns.length === 0 ? EMPTY_COLUMNS : state.editor.columns,

  /** The editor's working rows (referentially stable when empty). */
  selectEditorRows: ( state: SizeGuidesState ): Row[] =>
    state.editor.rows.length === 0 ? EMPTY_ROWS : state.editor.rows,

  /**
   * Structural validity of the editor's working guide:
   * exactly one main column (`mainColumnKey` set and present in `columns`) AND
   * unique non-empty main values across rows (mirrors aggregate INV-1..3).
   * `SIZE_GRID_ROW_ID` maps a variation to exactly one row, so duplicate/empty
   * main values are invalid.
   */
  selectIsGuideValid: ( state: SizeGuidesState ): boolean => {
    const mainKey = state.editor.mainColumnKey;
    if ( mainKey === null ) return false;

    const mainColumns = state.editor.columns.filter(
      ( column ) => column.id === mainKey
    );
    if ( mainColumns.length !== 1 ) return false;

    const seen = new Set<string>();
    for ( const row of state.editor.rows ) {
      const value = row.values[mainKey];
      if ( value === undefined || value === null || value === '' ) return false;
      const stringValue = String( value );
      if ( seen.has( stringValue )) return false;
      seen.add( stringValue );
    }
    return true;
  }
};
